import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme, useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { 
  Plane, 
  Calendar as CalendarIcon, 
  Ticket, 
  Plus, 
  Sun, 
  Moon, 
  LogOut,
  Trash2,
  Loader2,
  Menu,
  X,
  Search,
  Filter,
  Upload,
  FileText,
  Download
} from "lucide-react";
import AIAssistant from "../components/AIAssistant";

const Bookings = () => {
  const { theme, toggleTheme } = useTheme();
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadingFor, setUploadingFor] = useState(null);
  const [newBooking, setNewBooking] = useState({
    trip_id: "",
    type: "flight",
    title: "",
    provider: "",
    confirmation_code: "",
    booking_date: null,
    start_datetime: "",
    end_datetime: "",
    location: "",
    notes: ""
  });

  const resetNewBooking = () => {
    setNewBooking({
      trip_id: "",
      type: "flight",
      title: "",
      provider: "",
      confirmation_code: "",
      booking_date: null,
      start_datetime: "",
      end_datetime: "",
      location: "",
      notes: ""
    });
  };
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [bookingsRes, tripsRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/trips`, { headers })
      ]);
      setBookings(bookingsRes.data);
      setTrips(tripsRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento delle prenotazioni");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const handleCreateBooking = async () => {
    if (!newBooking.title || !newBooking.booking_date) {
      toast.error("Compila i campi obbligatori");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/bookings`, {
        ...newBooking,
        trip_id: newBooking.trip_id || null,
        booking_date: format(newBooking.booking_date, "yyyy-MM-dd")
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setBookings([response.data, ...bookings]);
      setCreateOpen(false);
      resetNewBooking();
      toast.success("Prenotazione salvata!");
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(bookings.filter(b => b.booking_id !== bookingId));
      toast.success("Prenotazione eliminata");
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleFileUpload = async (bookingId, file) => {
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Solo file PDF sono accettati");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Il file è troppo grande (max 10MB)");
      return;
    }

    setUploadingFor(bookingId);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/bookings/${bookingId}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Update local state
      setBookings(bookings.map(b => 
        b.booking_id === bookingId 
          ? { ...b, file_name: file.name, file_data: 'uploaded' }
          : b
      ));
      toast.success("File caricato con successo!");
    } catch (error) {
      toast.error("Errore nel caricamento del file");
    } finally {
      setUploadingFor(null);
    }
  };

  const handleFileDownload = async (bookingId, fileName) => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Decode base64 and create blob
      const byteCharacters = atob(response.data.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename || fileName || 'ticket.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Errore nel download del file");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const bookingTypes = [
    { value: "flight", label: "Volo", icon: "✈️" },
    { value: "train", label: "Treno", icon: "🚄" },
    { value: "bus", label: "Autobus", icon: "🚌" },
    { value: "hotel", label: "Hotel", icon: "🏨" },
    { value: "airbnb", label: "B&B/Airbnb", icon: "🏠" },
    { value: "other", label: "Altro", icon: "🎫" }
  ];

  const getBookingIcon = (type) => {
    const found = bookingTypes.find(t => t.value === type);
    return found ? found.icon : "🎫";
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesType = filterType === "all" || booking.type === filterType;
    const matchesSearch = searchQuery === "" || 
      booking.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (booking.confirmation_code && booking.confirmation_code.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (booking.provider && booking.provider.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesType && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
              <Link to="/" className="flex items-center gap-2">
                <Plane className="w-6 h-6 text-primary" />
                <span className="font-heading text-lg font-bold hidden sm:inline">TravelPro</span>
              </Link>
            </div>

            <div className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Dashboard
              </Link>
              <Link to="/bookings" className="text-sm font-medium text-primary">
                Prenotazioni
              </Link>
              <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Contatti
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="bookings-theme-toggle">
                {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={handleLogout} className="hidden md:flex" data-testid="bookings-logout-btn">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl px-4 py-4 space-y-2">
            <Link to="/dashboard" className="block py-2 text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>
              Dashboard
            </Link>
            <Link to="/bookings" className="block py-2 font-medium text-primary" onClick={() => setMobileMenuOpen(false)}>
              Prenotazioni
            </Link>
            <Link to="/contact" className="block py-2 text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>
              Contatti
            </Link>
            <Button variant="ghost" className="w-full justify-start" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        )}
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold">Prenotazioni</h1>
            <p className="text-muted-foreground mt-1">Gestisci i tuoi biglietti e prenotazioni</p>
          </div>
          
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="add-booking-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuova Prenotazione
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Aggiungi prenotazione</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tipo *</Label>
                    <Select value={newBooking.type} onValueChange={(v) => setNewBooking({...newBooking, type: v})}>
                      <SelectTrigger data-testid="booking-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {bookingTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.icon} {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Viaggio (opzionale)</Label>
                    <Select value={newBooking.trip_id || "none"} onValueChange={(v) => setNewBooking({...newBooking, trip_id: v === "none" ? "" : v})}>
                      <SelectTrigger data-testid="booking-trip-select">
                        <SelectValue placeholder="Nessun viaggio" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nessun viaggio</SelectItem>
                        {trips.map(trip => (
                          <SelectItem key={trip.trip_id} value={trip.trip_id}>
                            {trip.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="booking-title">Titolo *</Label>
                  <Input 
                    id="booking-title"
                    placeholder="Es. Volo Roma - Parigi"
                    value={newBooking.title}
                    onChange={(e) => setNewBooking({...newBooking, title: e.target.value})}
                    data-testid="booking-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="booking-provider">Fornitore</Label>
                    <Input 
                      id="booking-provider"
                      placeholder="Es. Ryanair"
                      value={newBooking.provider}
                      onChange={(e) => setNewBooking({...newBooking, provider: e.target.value})}
                      data-testid="booking-provider-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="booking-code">Codice conferma</Label>
                    <Input 
                      id="booking-code"
                      placeholder="Es. ABC123"
                      value={newBooking.confirmation_code}
                      onChange={(e) => setNewBooking({...newBooking, confirmation_code: e.target.value})}
                      className="font-mono"
                      data-testid="booking-code-input"
                    />
                  </div>
                </div>
                <div>
                  <Label>Data prenotazione *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start font-normal" data-testid="booking-date-btn">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {newBooking.booking_date ? format(newBooking.booking_date, "dd MMM yyyy", { locale: it }) : "Seleziona data"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={newBooking.booking_date}
                        onSelect={(date) => setNewBooking({...newBooking, booking_date: date})}
                        locale={it}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="booking-start">Data/ora inizio</Label>
                    <Input 
                      id="booking-start"
                      type="datetime-local"
                      value={newBooking.start_datetime}
                      onChange={(e) => setNewBooking({...newBooking, start_datetime: e.target.value})}
                      data-testid="booking-start-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="booking-end">Data/ora fine</Label>
                    <Input 
                      id="booking-end"
                      type="datetime-local"
                      value={newBooking.end_datetime}
                      onChange={(e) => setNewBooking({...newBooking, end_datetime: e.target.value})}
                      data-testid="booking-end-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="booking-location">Luogo</Label>
                  <Input 
                    id="booking-location"
                    placeholder="Es. Aeroporto Fiumicino"
                    value={newBooking.location}
                    onChange={(e) => setNewBooking({...newBooking, location: e.target.value})}
                    data-testid="booking-location-input"
                  />
                </div>
                <div>
                  <Label htmlFor="booking-notes">Note</Label>
                  <Textarea 
                    id="booking-notes"
                    placeholder="Aggiungi note..."
                    value={newBooking.notes}
                    onChange={(e) => setNewBooking({...newBooking, notes: e.target.value})}
                    data-testid="booking-notes-input"
                  />
                </div>
                <Button 
                  className="w-full" 
                  onClick={handleCreateBooking}
                  disabled={saving}
                  data-testid="booking-submit-btn"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Salva Prenotazione
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder="Cerca per titolo, codice o fornitore..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="booking-search-input"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full sm:w-[180px]" data-testid="booking-filter-select">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi</SelectItem>
              {bookingTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredBookings.length === 0 ? (
          <Card className="border-border">
            <CardContent className="py-16">
              <div className="text-center">
                <Ticket className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="font-heading text-lg font-semibold mb-2">Nessuna prenotazione</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery || filterType !== "all" 
                    ? "Nessun risultato per i filtri selezionati"
                    : "Inizia ad aggiungere le tue prenotazioni"
                  }
                </p>
                {!searchQuery && filterType === "all" && (
                  <Button onClick={() => setCreateOpen(true)} data-testid="empty-add-booking">
                    <Plus className="w-4 h-4 mr-2" />
                    Aggiungi Prenotazione
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredBookings.map((booking) => {
              const linkedTrip = trips.find(t => t.trip_id === booking.trip_id);
              
              return (
                <Card 
                  key={booking.booking_id} 
                  className="border-border hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-shadow relative group"
                  data-testid={`booking-card-${booking.booking_id}`}
                >
                  {/* Ticket perforation effect */}
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-6 bg-background rounded-r-full" />
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-6 bg-background rounded-l-full" />
                  
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{getBookingIcon(booking.type)}</span>
                        <div>
                          <h3 className="font-semibold">{booking.title}</h3>
                          {booking.provider && (
                            <p className="text-sm text-muted-foreground">{booking.provider}</p>
                          )}
                        </div>
                      </div>
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                            data-testid={`delete-booking-${booking.booking_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare questa prenotazione?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Questa azione non può essere annullata.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleDeleteBooking(booking.booking_id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Elimina
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>

                    {/* Dashed divider */}
                    <div className="border-t-2 border-dashed border-border my-4" />

                    <div className="space-y-2 text-sm">
                      {booking.confirmation_code && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Codice:</span>
                          <span className="font-mono font-medium">{booking.confirmation_code}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Data:</span>
                        <span className="font-mono">{format(new Date(booking.booking_date), "dd MMM yyyy", { locale: it })}</span>
                      </div>
                      {booking.location && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Luogo:</span>
                          <span className="truncate ml-2">{booking.location}</span>
                        </div>
                      )}
                      {linkedTrip && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Viaggio:</span>
                          <span className="text-primary truncate ml-2">{linkedTrip.title}</span>
                        </div>
                      )}
                    </div>

                    {booking.notes && (
                      <p className="text-xs text-muted-foreground mt-4 italic">{booking.notes}</p>
                    )}

                    {/* File upload/download section */}
                    <div className="border-t border-border mt-4 pt-4">
                      {booking.file_name || booking.file_data ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm">
                            <FileText className="w-4 h-4 text-primary" />
                            <span className="truncate max-w-[120px]">{booking.file_name || "ticket.pdf"}</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleFileDownload(booking.booking_id, booking.file_name)}
                            data-testid={`download-file-${booking.booking_id}`}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Scarica
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">Nessun biglietto allegato</span>
                          <label htmlFor={`file-upload-${booking.booking_id}`}>
                            <input
                              type="file"
                              id={`file-upload-${booking.booking_id}`}
                              accept=".pdf"
                              className="hidden"
                              onChange={(e) => handleFileUpload(booking.booking_id, e.target.files[0])}
                              disabled={uploadingFor === booking.booking_id}
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              asChild
                              disabled={uploadingFor === booking.booking_id}
                              data-testid={`upload-file-${booking.booking_id}`}
                            >
                              <span className="cursor-pointer">
                                {uploadingFor === booking.booking_id ? (
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                  <Upload className="w-4 h-4 mr-1" />
                                )}
                                Carica PDF
                              </span>
                            </Button>
                          </label>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <AIAssistant />
    </div>
  );
};

export default Bookings;
